import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { createRecord, deleteRecord, errorText, fetchList } from '../../registryApi';
import { labelFor } from '../../registryFormat';
import { POST_EMBARGO_ACCESS } from '../../registryChoices';

const GRANTS_API = '/registry/access_grants';

/**
 * Grant or revoke other organizations' post-embargo access to one experiment. Admin-only,
 * and works after the record locks (registration doesn't freeze who it's shared with).
 * Grants only apply to "By request" experiments — the API and
 * the BigQuery row access policy both ignore them otherwise.
 */
export default function ExperimentAccessDialog({ experiment, open, onClose, onChanged }) {
  const [grants, setGrants] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [toAdd, setToAdd] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const isByRequest = experiment?.post_embargo_access === 'by_request';

  const loadGrants = async () => {
    setGrants((await fetchList(`${GRANTS_API}?experiment=${experiment.id}`)) || []);
  };

  useEffect(() => {
    if (!open || !experiment) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setToAdd([]);
      try {
        const [grantList, orgList] = await Promise.all([
          fetchList(`${GRANTS_API}?experiment=${experiment.id}`),
          fetchList('/registry/organizations'),
        ]);
        if (cancelled) return;
        setGrants(grantList || []);
        setOrganizations(orgList || []);
      } catch (e) {
        if (!cancelled) setError('Could not load access for this experiment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, experiment]);

  // The registering org always has access as the owner: it's listed first, can't be removed,
  // and is never offered as a grant (any stray grant to it is folded into the owner row).
  const ownerId = String(experiment?.registering_organization);
  const sharedGrants = grants.filter((g) => String(g.organization) !== ownerId);
  const grantedIds = new Set(grants.map((g) => String(g.organization)));
  const options = organizations.filter(
    (org) =>
      String(org.id) !== ownerId && !grantedIds.has(String(org.id))
  );

  const handleGrant = async () => {
    setWorking(true);
    setError('');
    try {
      for (const org of toAdd) {
        await createRecord(GRANTS_API, { experiment: experiment.id, organization: org.id });
      }
      setToAdd([]);
      await loadGrants();
      onChanged?.();
    } catch (e) {
      setError(errorText(e, 'Could not grant access.'));
      await loadGrants().catch(() => null);
    } finally {
      setWorking(false);
    }
  };

  const handleRevoke = async (grant) => {
    setWorking(true);
    setError('');
    try {
      await deleteRecord(GRANTS_API, grant.id);
      await loadGrants();
      onChanged?.();
    } catch (e) {
      setError(errorText(e, 'Could not remove access.'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Manage access</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {experiment?.registry_id} · {experiment?.experiment_name || 'Untitled experiment'} ·{' '}
          {experiment?.registering_organization_name}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {!isByRequest && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This experiment&apos;s access is set to &ldquo;
                {labelFor(POST_EMBARGO_ACCESS, experiment?.post_embargo_access)}&rdquo;. Organizations can only be
                granted access individually when it&apos;s set to &ldquo;By request.&rdquo;
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Organizations with access
            </Typography>
            <List dense sx={{ border: 1, borderColor: 'divider', mb: 2 }}>
              <ListItem>
                <ListItemText
                  primary={`${experiment?.registering_organization_name || 'Registering organization'} (owner)`}
                />
              </ListItem>
              {sharedGrants.map((grant) => (
                <ListItem
                  key={grant.id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      color="error"
                      aria-label={`Remove access for ${grant.organization_name}`}
                      disabled={working}
                      onClick={() => handleRevoke(grant)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText primary={grant.organization_name} />
                </ListItem>
              ))}
            </List>

            {isByRequest && (
              <>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={options}
                    getOptionLabel={(org) => org.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={toAdd}
                    onChange={(event, value) => setToAdd(value)}
                    disabled={working}
                    fullWidth
                    size="small"
                    renderTags={(value, getTagProps) =>
                      value.map((org, index) => {
                        const { key, ...chipProps } = getTagProps({ index });
                        return <Chip key={key} label={org.name} size="small" {...chipProps} />;
                      })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Add organizations" placeholder="Search organizations…" />
                    )}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleGrant}
                    disabled={working || toAdd.length === 0}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0, py: '7px' }}
                  >
                    Grant access
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Granted organizations can view the experiment once it&apos;s registered and its embargo has
                  ended. Changes take effect right away and are logged.
                </Typography>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={working}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
